import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  CircularProgress,
  alpha,
  useTheme,
  Modal,
  Backdrop,
  Stack,
} from '@mui/material';
import { useState, useRef } from 'react';
import { genericFormProps } from '../signup.types';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import LinkIcon from '@mui/icons-material/Link';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useProductData } from '../../../hooks/useProductData';
import { useAuth } from '../../../hooks/useAuth';
import { toast } from 'react-toastify';
import LoadingPage from '../../LoadingPage';
import { publishProduct, updateProduct } from '../../../utils/api';

interface ProductsFormProps extends genericFormProps {
  formSubmitting: boolean;
  showSteps?: boolean;
}

interface ProductLink {
  url: string;
  name?: string;
  description?: string;
  imageURL?: string;
  image?: File | null;
  status: 'idle' | 'processing' | 'success' | 'error';
  error?: string;
  id?: string; // Backend product ID after publishing
  isPublished?: boolean; // Track if product has been published to backend
}

const MAX_FILE_BYTES = 15 * 1024 * 1024; // 15 MB
const ALLOWED_FORMATS = new Set(['image/jpeg', 'image/jpg', 'image/png']);

const ProductsForm: React.FC<ProductsFormProps> = ({
  values,
  setFieldValue,
  formSubmitting,
}) => {
  const theme = useTheme();
  const { authUser } = useAuth();
  const { handleExtractProduct, productInfo, setProductInfo } =
    useProductData();

  const [currentLink, setCurrentLink] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductLink | null>(
    null
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Image modal states
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [tempImage, setTempImage] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imgErr, setImgErr] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Form validation errors
  const [errors, setErrors] = useState<{ name?: string; description?: string }>(
    {}
  );
  const [isPublishing, setIsPublishing] = useState(false);

  const productLinks: ProductLink[] = values.productLinks || [];

  const normalizeUrl = (raw: string) => {
    const s = raw.trim();
    if (!s) return s;
    if (!/^[a-zA-Z][\w+.-]*:/.test(s)) return `https://${s}`;
    return s;
  };

  const validateUrl = (raw: string): string | null => {
    const s = raw.trim();
    if (!s) return 'Please enter a URL';
    try {
      const normalized = normalizeUrl(s);
      const u = new URL(normalized);
      if (!/^https?:$/.test(u.protocol))
        return 'URL must start with http or https';
      if (!u.hostname || !u.hostname.includes('.'))
        return 'Enter a valid domain';
      return null;
    } catch {
      return 'Enter a valid URL';
    }
  };

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_FORMATS.has(file.type)) {
      return 'Only JPG and PNG files are allowed.';
    }
    if (file.size > MAX_FILE_BYTES) {
      return 'File is too large. Maximum size allowed is 15 MB.';
    }
    return null;
  };

  const handleAddProduct = async () => {
    const error = validateUrl(currentLink);
    if (error) {
      setLinkError(error);
      return;
    }

    const normalized = normalizeUrl(currentLink);

    // Check for duplicates
    if (productLinks.some((p) => p.url === normalized)) {
      setLinkError('This product link has already been added');
      return;
    }

    setLinkError(null);
    setIsExtracting(true);

    try {
      // Extract product details and use the returned data directly
      // (avoid race condition with productInfo state)
      const extractedData = await handleExtractProduct(
        authUser?.uid ?? '',
        normalized
      );

      // Create product with extracted data from the returned value
      const newProduct: ProductLink = {
        url: normalized,
        name: extractedData?.name || '',
        description: extractedData?.description || '',
        imageURL: extractedData?.imageURL || '',
        image: null,
        status: 'idle',
      };

      setEditingProduct(newProduct);
      setEditingIndex(null); // null means new product
      setTempImage(newProduct.imageURL || '');
      setShowEditModal(true);
      setCurrentLink('');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to extract product details');
      setLinkError('Failed to extract product details');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleEditProduct = (product: ProductLink, index: number) => {
    setEditingProduct({ ...product });
    setEditingIndex(index);
    setTempImage(product.imageURL || '');
    setSelectedFile(product.image || null);
    setShowEditModal(true);
  };

  const handleSaveProduct = async () => {
    if (!editingProduct) return;

    // Validate
    const newErrors: { name?: string; description?: string } = {};
    if (!editingProduct.name?.trim()) {
      newErrors.name = 'Product title is required';
    }
    if (!editingProduct.description?.trim()) {
      newErrors.description = 'Product description is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsPublishing(true);

    try {
      if (editingIndex !== null && productLinks[editingIndex]?.isPublished) {
        // Update existing published product
        const existingProduct = productLinks[editingIndex];
        const updatePayload = {
          user_id: authUser?.uid,
          product_id: existingProduct.id,
          name: editingProduct.name,
          description: editingProduct.description,
          ...(editingProduct.image ? { image: editingProduct.image } : {}),
        };
        
        await updateProduct(authUser?.uid ?? '', updatePayload);
        
        const updatedProduct: ProductLink = {
          ...editingProduct,
          id: existingProduct.id,
          status: 'success',
          isPublished: true,
        };
        
        const updated = [...productLinks];
        updated[editingIndex] = updatedProduct;
        setFieldValue('productLinks', updated);
        toast.success('Product updated!');
      } else {
        // Publish new product to backend
        const productPayload = {
          productURL: editingProduct.url,
          name: editingProduct.name || '',
          description: editingProduct.description || '',
          imageURL: editingProduct.imageURL || '',
          image: editingProduct.image || null,
        };

        const result = await publishProduct(authUser?.uid ?? '', productPayload as any);
        
        // Extract the product ID from the response
        const productId = result?.product?.id || result?.product_id || result?.id;
        
        const publishedProduct: ProductLink = {
          ...editingProduct,
          id: productId,
          status: 'success',
          isPublished: true,
        };

        if (editingIndex !== null) {
          // Update existing local product with published data
          const updated = [...productLinks];
          updated[editingIndex] = publishedProduct;
          setFieldValue('productLinks', updated);
        } else {
          // Add new published product
          setFieldValue('productLinks', [...productLinks, publishedProduct]);
        }
        toast.success('Product published successfully!');
      }

      handleCloseEditModal();
    } catch (error: any) {
      console.error('Error publishing product:', error);
      toast.error(error?.message || 'Failed to publish product. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingProduct(null);
    setEditingIndex(null);
    setErrors({});
    setProductInfo(null);
  };

  const handleRemoveProduct = (index: number) => {
    const updated = productLinks.filter((_, i) => i !== index);
    setFieldValue('productLinks', updated);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddProduct();
    }
  };

  // Image handling
  const openImageModal = () => {
    setTempImage(editingProduct?.imageURL || '');
    setImgErr(null);
    setSelectedFile(editingProduct?.image || null);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setImgErr(null);
  };

  const handleSaveImage = () => {
    if (!tempImage && !selectedFile) {
      setImgErr('Please select an image');
      return;
    }

    if (editingProduct) {
      setEditingProduct({
        ...editingProduct,
        image: selectedFile,
        imageURL: selectedFile ? URL.createObjectURL(selectedFile) : tempImage,
      });
    }

    closeImageModal();
  };

  const handleFileBrowse = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) {
      setImgErr(err);
      return;
    }
    setImgErr(null);
    setSelectedFile(file);
    setTempImage(URL.createObjectURL(file));
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) {
      setImgErr(err);
      setSelectedFile(null);
      return;
    }
    setImgErr(null);
    setSelectedFile(file);
    setTempImage(URL.createObjectURL(file));
  };

  const updateEditingProduct = <K extends keyof ProductLink>(
    key: K,
    value: ProductLink[K]
  ) => {
    if (!editingProduct) return;
    setEditingProduct({ ...editingProduct, [key]: value });

    // Clear errors when user types
    if (key === 'name' && errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
    if (key === 'description' && errors.description) {
      setErrors((prev) => ({ ...prev, description: undefined }));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {/* Page Title */}
      <Box>
        <Typography
          variant='h5'
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          Products
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
          }}
        >
          Increase revenue with automated promotions during chats.
        </Typography>
        <Typography
          sx={{
            color: 'primary.secondary',
            fontSize: 16,
            fontFamily: '"Merriweather Sans", sans-serif',
            fontWeight: 600,
            lineHeight: '20.8px',
            letterSpacing: '0.15px',
            mb: 2,
            mt: 2,
          }}
        >
          *Optional
        </Typography>
      </Box>

      {/* Add Product Section */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
          }}
        >
          <TextField
            fullWidth
            placeholder='https://example.com/product'
            value={currentLink}
            onChange={(e) => {
              setCurrentLink(e.target.value);
              if (linkError) setLinkError(null);
            }}
            onKeyPress={handleKeyPress}
            error={Boolean(linkError)}
            helperText={linkError || ' '}
            disabled={formSubmitting || isExtracting}
            size='small'
            InputProps={{
              startAdornment: (
                <LinkIcon
                  sx={{ mr: 1, color: 'text.disabled', fontSize: 20 }}
                />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.default',
              },
            }}
          />
          <Button
            variant='contained'
            onClick={handleAddProduct}
            disabled={!currentLink.trim() || formSubmitting || isExtracting}
            startIcon={
              isExtracting ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <AddIcon />
              )
            }
            sx={{
              minWidth: { xs: '100%', sm: '120px' },
              height: '40px',
              textTransform: 'none',
            }}
          >
            {isExtracting ? 'Loading...' : 'Add'}
          </Button>
        </Box>
      </Paper>

      {/* Product List */}
      {productLinks.length > 0 && (
        <Box sx={{ width: '100%' }}>
          <Typography
            variant='subtitle2'
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 2,
            }}
          >
            Added Products ({productLinks.length})
          </Typography>

          <Box
            sx={{
              borderTop: 1,
              borderColor: (t) => t.palette.divider,
            }}
          >
            {productLinks.map((product, index) => (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '50px 1fr auto',
                  alignItems: 'center',
                  columnGap: 2,
                  px: 1,
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: (t) => t.palette.divider,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  },
                }}
              >
                {/* Thumbnail */}
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    overflow: 'hidden',
                    borderRadius: 1.5,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: product.imageURL
                      ? 'transparent'
                      : alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  {product.imageURL ? (
                    <img
                      src={product.imageURL}
                      alt={product.name ?? ''}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 8,
                      }}
                    />
                  ) : (
                    <ShoppingBagOutlinedIcon
                      sx={{ fontSize: 24, color: 'primary.main' }}
                    />
                  )}
                </Box>

                {/* Title - wraps naturally */}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 500,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflow: 'visible',
                      lineHeight: 1.3,
                    }}
                    title={product.name}
                  >
                    {product.name || 'Untitled Product'}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <IconButton
                    size='small'
                    onClick={() => handleEditProduct(product, index)}
                    disabled={formSubmitting}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={() => handleRemoveProduct(index)}
                    disabled={formSubmitting}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'error.main',
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                      },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Edit Product Modal */}
      <Modal
        open={showEditModal}
        onClose={handleCloseEditModal}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: '90vw', md: '600px' },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 3,
            p: 3,
            overflow: 'auto',
          }}
        >
          <Typography variant='h6' sx={{ mb: 3 }}>
            {editingIndex !== null ? 'Edit Product' : 'Add Product'}
          </Typography>

          {/* Product Link (readonly) */}
          <Box sx={{ mb: 2 }}>
            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
              Product Link
            </Typography>
            <TextField
              fullWidth
              value={editingProduct?.url || ''}
              disabled
              size='small'
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'action.disabledBackground',
                },
              }}
            />
          </Box>

          {/* Product Image */}
          <Box sx={{ mb: 2 }}>
            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
              Product Image
            </Typography>
            <Box
              sx={{
                position: 'relative',
                width: { xs: '100%', sm: 200 },
                aspectRatio: '16 / 9',
                border: `2px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                '&:hover .edit-overlay': { opacity: 1 },
              }}
              onClick={openImageModal}
            >
              {editingProduct?.imageURL ? (
                <img
                  src={editingProduct.imageURL}
                  alt={editingProduct.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    Add Image
                  </Typography>
                </Box>
              )}
              <Box
                className='edit-overlay'
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: '#fff',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                }}
              >
                <Typography variant='body2'>Edit image</Typography>
              </Box>
            </Box>
          </Box>

          {/* Product Title */}
          <Box sx={{ mb: 2 }}>
            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
              Product Title <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              value={editingProduct?.name || ''}
              onChange={(e) => updateEditingProduct('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              size='small'
              placeholder='Enter product title'
            />
          </Box>

          {/* Product Description */}
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
              Description <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ mb: 1, display: 'block' }}
            >
              Be as descriptive as possible to help AI better pitch the product!
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={editingProduct?.description || ''}
              onChange={(e) =>
                updateEditingProduct('description', e.target.value)
              }
              error={!!errors.description}
              helperText={errors.description}
              placeholder='Enter product description'
            />
          </Box>

          {/* Actions */}
          <Stack direction='row' spacing={2} justifyContent='flex-end'>
            <Button onClick={handleCloseEditModal} variant='outlined' disabled={isPublishing}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveProduct} 
              variant='contained'
              disabled={isPublishing}
              startIcon={isPublishing ? <CircularProgress size={16} color='inherit' /> : null}
            >
              {isPublishing ? 'Publishing...' : (editingIndex !== null ? 'Save' : 'Add Product')}
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Image Edit Modal */}
      <Modal
        open={imageModalOpen}
        onClose={closeImageModal}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: 600,
            width: '90vw',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 3,
            p: 3,
          }}
        >
          <Typography variant='h6' gutterBottom>
            Update product image
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            {/* Preview */}
            <Box>
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  aspectRatio: '16 / 9',
                  border: `1px solid ${imgErr ? theme.palette.error.main : theme.palette.divider}`,
                  bgcolor: 'background.default',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {tempImage ? (
                  <img
                    src={tempImage}
                    alt='Preview'
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    No image selected
                  </Typography>
                )}
              </Box>
              {imgErr && (
                <Typography
                  variant='caption'
                  color='error'
                  sx={{ mt: 1, display: 'block' }}
                >
                  {imgErr}
                </Typography>
              )}
            </Box>

            {/* Dropzone */}
            <Box
              sx={{
                border: '2px dashed',
                borderColor: imgErr ? 'error.main' : 'divider',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <Typography
                variant='body1'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <UploadFileIcon fontSize='small' />
                Drop JPG/PNG
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                or
              </Typography>
              <Button
                variant='outlined'
                onClick={handleFileBrowse}
                startIcon={<UploadFileIcon />}
              >
                Browse Files
              </Button>
              <input
                type='file'
                accept='.jpg,.jpeg,.png'
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Box>
          </Box>

          <Stack
            direction='row'
            spacing={1}
            justifyContent='flex-end'
            sx={{ mt: 3 }}
          >
            <Button onClick={closeImageModal}>Cancel</Button>
            <Button variant='contained' onClick={handleSaveImage}>
              Save
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Extracting Modal */}
      <Modal
        open={isExtracting}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 3,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant='h6'>Processing Link...</Typography>
          <Typography variant='body2' color='text.secondary'>
            Please wait, fetching product details...
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default ProductsForm;
